import { Card, Spinner, SpinnerSize } from '@blueprintjs/core';
import classNames from 'classnames';
import * as React from 'react';
import { Text } from 'vet-bones/components';

import { getDataSourceCardTestId } from 'src/constants/testIds';
import { dataSourcesByTypeSelector } from 'src/selectors/dataSourcesByType';
import { DataSource } from 'src/store/state/dataSources';
import { components } from 'src/templates/copy';
import { useReduxSelector } from 'src/utils/hooks/useReduxSelector';

type DataSourceCardsProps = {
  isLoading: boolean;
  type: string;
  onCreateDataSource?: () => void;
  onSelectDataSource: (dataSource: DataSource) => void;
};

export const CREATE_NEW_DATA_SOURCE = {
  id: 'CREATE_NEW_DATA_SOURCE',
  options: { 'jdbc.url': '', 'jdbc.username': '' },
};

export const DataSourceCards: React.VFC<DataSourceCardsProps> = ({
  isLoading,
  onCreateDataSource,
  onSelectDataSource,
  type,
}) => {
  const dataSourcesByType = useReduxSelector(dataSourcesByTypeSelector);

  const dataSources = React.useMemo(
    () => dataSourcesByType[type] || [],
    [dataSourcesByType, type]
  );

  const cardList: React.ReactElement[] = [
    ...(onCreateDataSource ? [CREATE_NEW_DATA_SOURCE] : []),
    ...dataSources,
  ].map((dataSource, idx) => {
    return (
      <Card
        key={dataSource.id}
        className={classNames({
          'sd-data-source-panel-cards__card': true,
          'sd-data-source-panel-cards__card--source': true,
          'sd-data-source-panel-cards__card--create-new':
            dataSource === CREATE_NEW_DATA_SOURCE,
        })}
        data-testid={getDataSourceCardTestId(dataSource.id, idx)}
        interactive
        onClick={() => {
          if (dataSource === CREATE_NEW_DATA_SOURCE) {
            onCreateDataSource?.();
          } else {
            onSelectDataSource(dataSource as DataSource);
          }
        }}
      >
        <b>
          <Text ellipsize>
            {dataSource === CREATE_NEW_DATA_SOURCE
              ? components.projectResourceDialog.projectDataSourcePanel
                  .createNewDataSource
              : dataSource.id}
          </Text>
        </b>
        {dataSource === CREATE_NEW_DATA_SOURCE ? null : (
          <>
            <Text ellipsize>
              {components.projectResourceDialog.projectDataSourcePanel.endpoint}
              : {dataSource.options?.['jdbc.url'] || ''}
            </Text>
          </>
        )}
      </Card>
    );
    // TODO: Need a more general approach to showing url / username
    // information when non-JDBC data sources are supported
  });

  if (isLoading) {
    return <Spinner size={SpinnerSize.LARGE} />;
  }

  return (
    <div className={classNames('sd-dialog-tile', 'sd-data-source-panel-cards')}>
      {cardList}
    </div>
  );
};
